import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Table,
  InputNumber,
  Space,
  Divider,
  Tag,
  Popconfirm,
  Empty,
  Radio,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllProductCategory } from "../../redux/actions/productCategory/getProductCategoryAction";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import { loadAllCustomer } from "../../redux/actions/customer/getCustomerAction";
import { loadRunningSize } from "../../redux/actions/product/getAllProductAction";
import axios from "axios";
import data from "../../data/rawMaterialData";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import { buildLayersForPly } from "../../utils/layerUtils";

const { Title, Text } = Typography;

const BulkAddRunningSize = () => {
  const { unitType, ply, castingType } = data;
  const layerObj = { layerId: 0, layerType: "", materialType: "", gsm: "", bf: "", ff: "" };

  const category = useSelector((state) => state.productCategories?.list);
  const subCategory = useSelector((state) => state.productSubCategories?.list);
  const customer = useSelector((state) => state.customers?.list);
  const runningSizes = useSelector((state) => state.products?.list);
  const dispatch = useDispatch();

  // Form states
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Common fields state (Step 1)
  const [commonFields, setCommonFields] = useState({
    length: null,
    width: null,
    height: null,
    unitType: null,
    castingType: null,
    quantity: null,
    lastSalePrice: null,
    ply: null,
    isPrinting: false,
    isCoating: false,
    layerSelector: false,
    topConversionRate: null,
    boxConversionRate: null,
    rs: null,
    cs: null,
    boxesPerSheet: null,
    sheetsPerBox: null,
    remarks: null,
    boxType: null,
    rateType: "individual",
    packingQuantity: null,
    joiningMethod: null,
  });

  // Layer/Printing/Coating states
  const [layers, setLayers] = useState([]);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printing, setPrinting] = useState({});
  const [isCoating, setIsCoating] = useState(false);
  const [coating, setCoating] = useState({ coatings: [] });
  const [layerSelector, setLayerSelector] = useState(false);

  // Load initial data
  useEffect(() => {
    dispatch(loadAllProductCategory({ page: 1, limit: 100 }));
    dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
    dispatch(loadAllCustomer({ page: 1, limit: 100 }));
    dispatch(loadRunningSize({ page: 1, limit: 1000, status: true }));
  }, [dispatch]);

  // Ply change: rebuild layers array
  const handlePlyChange = (plyVal) => {
    setCommonFields({ ...commonFields, ply: plyVal });
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(plyVal, layers, layerObj);
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  // Layer selector toggle
  const handleSelectorChange = (e) => {
    if (e.target.value === "advance") {
      setLayerSelector(true);
      setCommonFields({ ...commonFields, layerSelector: true });
    } else {
      setLayerSelector(false);
      setCommonFields({ ...commonFields, layerSelector: false });
    }
  };

  // Printing toggle
  const handlePrintingChange = (e) => {
    if (e.target.value === "yes") {
      setIsPrinting(true);
      setCommonFields({ ...commonFields, isPrinting: true });
    } else {
      setIsPrinting(false);
      setPrinting({});
      setCommonFields({ ...commonFields, isPrinting: false });
    }
  };

  // Coating toggle
  const handleCoatingChange = (e) => {
    if (e.target.value === "yes") {
      setIsCoating(true);
      setCommonFields({ ...commonFields, isCoating: true });
    } else {
      setIsCoating(false);
      setCoating({ coatings: [] });
      setCommonFields({ ...commonFields, isCoating: false });
    }
  };

  // Handle proceeding to step 2
  const handleProceedToStep2 = async () => {
    try {
      const values = await form.validateFields([
        "subcategory",
        "customer",
      ]);

      setCommonFields({
        ...commonFields,
        subcategory: values.subcategory,
        customer: values.customer,
      });

      setCurrentStep(2);
      toast.success("Proceeding to Step 2: Add Running Size Details");
    } catch (error) {
      toast.error("Please fill required fields (Subcategory and Customer)");
    }
  };

  // Add a new row for step 2
  const addRow = () => {
    const newRow = {
      key: Date.now(),
      name: "",
      length: commonFields.length,
      width: commonFields.width,
      height: commonFields.height,
      unitType: commonFields.unitType,
      castingType: commonFields.castingType,
      quantity: commonFields.quantity,
      lastSalePrice: commonFields.lastSalePrice,
      ply: commonFields.ply,
    };
    setRows([...rows, newRow]);
  };

  // Update row data
  const updateRow = (key, field, value) => {
    setRows(
      rows.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  // Delete a row
  const deleteRow = (key) => {
    setRows(rows.filter((row) => row.key !== key));
  };

  // Handle bulk submission
  const handleBulkSubmit = async () => {
    if (rows.length === 0) {
      toast.error("Please add at least one running size");
      return;
    }

    // Validate that all rows have unique names
    const names = rows.map((r) => r.name);
    const uniqueNames = new Set(names);
    if (uniqueNames.size !== names.length) {
      toast.error("Running size names must be unique");
      return;
    }

    // Check for empty required fields
    const invalidRows = rows.filter((row) => !row.name || !row.length || !row.width || !row.height);
    if (invalidRows.length > 0) {
      toast.error("All rows must have Name, Length, Width, and Height filled");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        commonFields: {
          subcategory: commonFields.subcategory,
          customer: commonFields.customer,
          isPrinting: commonFields.isPrinting,
          isCoating: commonFields.isCoating,
          layerSelector: commonFields.layerSelector,
          layers: JSON.stringify(layers),
          printing: JSON.stringify(printing),
          coating: JSON.stringify(coating),
          topConversionRate: commonFields.topConversionRate,
          boxConversionRate: commonFields.boxConversionRate,
          rs: commonFields.rs,
          cs: commonFields.cs,
          boxesPerSheet: commonFields.boxesPerSheet,
          sheetsPerBox: commonFields.sheetsPerBox,
          remarks: commonFields.remarks,
          boxType: commonFields.boxType,
          rateType: commonFields.rateType,
          packingQuantity: commonFields.packingQuantity,
          joiningMethod: commonFields.joiningMethod,
        },
        rows: rows.map((row) => ({
          name: row.name,
          length: parseFloat(row.length),
          width: parseFloat(row.width),
          height: parseFloat(row.height),
          unitType: row.unitType,
          castingType: row.castingType,
          quantity: row.quantity ? parseInt(row.quantity) : null,
          lastSalePrice: row.lastSalePrice ? parseFloat(row.lastSalePrice) : null,
          ply: row.ply,
        })),
      };

      const response = await axios.post("/running-size/bulk-add", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(`Successfully created ${rows.length} running sizes!`);
        resetForm();
      } else {
        toast.error("Failed to create running sizes");
      }
    } catch (error) {
      console.error("Bulk add error:", error);
      toast.error(
        error.response?.data?.message ||
          `Error: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset entire form
  const resetForm = () => {
    form.resetFields();
    setCurrentStep(1);
    setRows([]);
    setCommonFields({
      length: null,
      width: null,
      height: null,
      unitType: null,
      castingType: null,
      quantity: null,
      lastSalePrice: null,
      ply: null,
      isPrinting: false,
      isCoating: false,
      layerSelector: false,
      topConversionRate: null,
      boxConversionRate: null,
      rs: null,
      cs: null,
      boxesPerSheet: null,
      sheetsPerBox: null,
      remarks: null,
      boxType: null,
      rateType: "individual",
      packingQuantity: null,
      joiningMethod: null,
    });
    setLayers([]);
    setGsm([]);
    setBf([]);
    setIsPrinting(false);
    setPrinting({});
    setIsCoating(false);
    setCoating({ coatings: [] });
    setLayerSelector(false);
  };

  // Go back to step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setRows([]);
  };

  // Table columns for step 2
  const tableColumns = [
    {
      title: "Running Size Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => updateRow(record.key, "name", e.target.value)}
          placeholder="e.g., RS-001"
          required
        />
      ),
    },
    {
      title: "Length",
      dataIndex: "length",
      key: "length",
      width: 100,
      render: (text, record) =>
        commonFields.length ? (
          <span>{commonFields.length}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "length", val)}
            placeholder="Length"
            required
          />
        ),
    },
    {
      title: "Width",
      dataIndex: "width",
      key: "width",
      width: 100,
      render: (text, record) =>
        commonFields.width ? (
          <span>{commonFields.width}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "width", val)}
            placeholder="Width"
            required
          />
        ),
    },
    {
      title: "Height",
      dataIndex: "height",
      key: "height",
      width: 100,
      render: (text, record) =>
        commonFields.height ? (
          <span>{commonFields.height}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "height", val)}
            placeholder="Height"
            required
          />
        ),
    },
    {
      title: "Unit Type",
      dataIndex: "unitType",
      key: "unitType",
      width: 120,
      render: (text, record) =>
        commonFields.unitType ? (
          <span>{commonFields.unitType}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "unitType", val)}
            placeholder="Select"
            style={{ width: '100%' }}
            options={unitType?.map((u) => ({ label: u, value: u })) || []}
          />
        ),
    },
    {
      title: "Casting Type",
      dataIndex: "castingType",
      key: "castingType",
      width: 120,
      render: (text, record) =>
        commonFields.castingType ? (
          <span>{commonFields.castingType}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "castingType", val)}
            placeholder="Select"
            style={{ width: '100%' }}
            options={castingType?.map((c) => ({ label: c.name, value: c.value })) || []}
          />
        ),
    },
    {
      title: "Avg Order Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (text, record) =>
        commonFields.quantity ? (
          <span>{commonFields.quantity}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "quantity", val)}
            placeholder="Qty"
          />
        ),
    },
    {
      title: "Last Sale Price",
      dataIndex: "lastSalePrice",
      key: "lastSalePrice",
      width: 120,
      render: (text, record) =>
        commonFields.lastSalePrice ? (
          <span>{commonFields.lastSalePrice}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "lastSalePrice", val)}
            placeholder="Price"
          />
        ),
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      width: 80,
      render: (text, record) =>
        commonFields.ply ? (
          <span>{commonFields.ply}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "ply", val)}
            placeholder="Select"
            style={{ width: '100%' }}
            options={ply?.map((p) => ({ label: p.name, value: p.value })) || []}
          />
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Popconfirm
          title="Delete Row?"
          description="Are you sure you want to delete this row?"
          onConfirm={() => deleteRow(record.key)}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        title={
          <Title level={3}>
            {currentStep === 1 ? "Bulk Add Running Sizes - Step 1" : "Bulk Add Running Sizes - Step 2"}
          </Title>
        }
      >
        {/* STEP 1: Common Fields */}
        {currentStep === 1 && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              layerSelector: "basic",
              print_selector: "no",
              isCoating: "no",
            }}
          >
            <Row gutter={24}>
              {/* LEFT COLUMN — Sizing & Config */}
              <Col xs={24} lg={10}>
                <Card bordered={false} title="Sizing & Configuration">
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="subcategory"
                        label="Product Sub Category"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select Sub Category"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children?.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {subCategory?.map((sub) => (
                            <Select.Option key={sub.id} value={sub.id}>
                              {sub.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="customer"
                        label="Customer"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select Customer"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children?.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {customer?.map((cust) => (
                            <Select.Option key={cust.id} value={cust.id}>
                              {cust.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider style={{ margin: "8px 0 16px" }}>Optional Common Values (leave blank for unique per size)</Divider>

                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item name="length" label="Length (Optional)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Common"
                          onChange={(val) => setCommonFields({ ...commonFields, length: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="width" label="Width (Optional)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Common"
                          onChange={(val) => setCommonFields({ ...commonFields, width: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="height" label="Height (Optional)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Common"
                          onChange={(val) => setCommonFields({ ...commonFields, height: val })}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="unitType" label="Unit (Optional)">
                        <Select
                          placeholder="Unit Type"
                          allowClear
                          onChange={(val) => setCommonFields({ ...commonFields, unitType: val })}
                        >
                          {unitType?.map((u) => (
                            <Select.Option key={u} value={u}>{u}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="castingType" label="Casting (Optional)">
                        <Select
                          placeholder="Casting Type"
                          allowClear
                          onChange={(val) => setCommonFields({ ...commonFields, castingType: val })}
                        >
                          {castingType?.map((c, i) => (
                            <Select.Option key={i} value={c.value}>{c.name}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="quantity" label="Avg Order Qty (Optional)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Common quantity"
                          onChange={(val) => setCommonFields({ ...commonFields, quantity: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="lastSalePrice" label="Last Sale Price (Optional)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Common price"
                          onChange={(val) => setCommonFields({ ...commonFields, lastSalePrice: val })}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="topConversionRate" label="Top Conversion Rate /Kg">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="e.g., 65"
                          onChange={(val) => setCommonFields({ ...commonFields, topConversionRate: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="boxConversionRate" label="Box Conversion Rate /Kg">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="e.g., 65"
                          onChange={(val) => setCommonFields({ ...commonFields, boxConversionRate: val })}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="rs" label="RS">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Optional"
                          onChange={(val) => setCommonFields({ ...commonFields, rs: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="cs" label="CS">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Optional"
                          onChange={(val) => setCommonFields({ ...commonFields, cs: val })}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="boxesPerSheet" label="Boxes per Sheet (m)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Optional"
                          onChange={(val) => setCommonFields({ ...commonFields, boxesPerSheet: val })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="sheetsPerBox" label="Sheets per Box (n)">
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Optional"
                          onChange={(val) => setCommonFields({ ...commonFields, sheetsPerBox: val })}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="remarks" label="Remarks">
                    <Input.TextArea
                      rows={2}
                      placeholder="Optional remarks"
                      onChange={(e) => setCommonFields({ ...commonFields, remarks: e.target.value })}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="boxType" label="Box Type">
                        <Select
                          placeholder="Select Box Type"
                          allowClear
                          onChange={(val) => setCommonFields({ ...commonFields, boxType: val })}
                        >
                          <Select.Option value="inner">Inner</Select.Option>
                          <Select.Option value="outer">Outer</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="rateType" label="Rate Type">
                        <Select
                          placeholder="Select Rate Type"
                          onChange={(val) => setCommonFields({ ...commonFields, rateType: val })}
                        >
                          <Select.Option value="individual">Individual</Select.Option>
                          <Select.Option value="innerwise">Innerwise</Select.Option>
                          <Select.Option value="outerwise">Outerwise</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {commonFields.rateType && commonFields.rateType !== "individual" && (
                    <Form.Item name="packingQuantity" label="No. of Inners">
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="No. of inners per outer"
                        onChange={(val) => setCommonFields({ ...commonFields, packingQuantity: val })}
                      />
                    </Form.Item>
                  )}

                  <Form.Item name="joiningMethod" label="Glueing">
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={(e) => setCommonFields({ ...commonFields, joiningMethod: e.target.value })}
                    >
                      <Radio.Button value="stitching">Stitching</Radio.Button>
                      <Radio.Button value="hand_pasting">Hand Pasting</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Card>
              </Col>

              {/* RIGHT COLUMN — Customization */}
              <Col xs={24} lg={14}>
                <Card bordered={false} title="Customization">
                  <Form.Item
                    name="ply"
                    label="Ply/ Walls"
                    style={{ marginBottom: "15px" }}
                  >
                    <Select
                      placeholder="Ply/ Walls"
                      onChange={handlePlyChange}
                    >
                      {ply?.map((p) => (
                        <Select.Option key={p.value} value={p.value}>
                          {p.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <LayerTemplateSelector
                    ply={layers.length}
                    layers={layers}
                    setLayers={setLayers}
                    gsm={gsm}
                    setGsm={setGsm}
                    bf={bf}
                    setBf={setBf}
                  />

                  <Form.Item
                    label="Layer Selector"
                    name="layerSelector"
                    style={{ marginBottom: "15px" }}
                  >
                    <Radio.Group onChange={handleSelectorChange}>
                      <Radio value="basic">Basic</Radio>
                      <Radio value="advance">Advance</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {!layerSelector ? (
                    <LayerSelectorBasic
                      layers={layers}
                      setLayers={setLayers}
                      layerSelector={layerSelector}
                    />
                  ) : (
                    <LayerSelectorAdvance
                      layers={layers}
                      gsm={gsm}
                      bf={bf}
                      setLayers={setLayers}
                      setGsm={setGsm}
                      setBf={setBf}
                      layerSelector={layerSelector}
                    />
                  )}

                  <Form.Item
                    label="Printing"
                    name="print_selector"
                    style={{ marginBottom: "15px" }}
                  >
                    <Radio.Group onChange={handlePrintingChange}>
                      <Radio value="yes">Yes</Radio>
                      <Radio value="no">No</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {isPrinting && (
                    <PrintSelector
                      printing={printing}
                      setPrinting={setPrinting}
                      isPrinting={isPrinting}
                    />
                  )}

                  <Form.Item
                    label="Coating"
                    name="isCoating"
                    style={{ marginBottom: "15px" }}
                  >
                    <Radio.Group onChange={handleCoatingChange}>
                      <Radio value="yes">Yes</Radio>
                      <Radio value="no">No</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {isCoating && (
                    <CoatingSelector
                      coating={coating}
                      setCoating={setCoating}
                      isCoating={isCoating}
                    />
                  )}
                </Card>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16} justify="end" style={{ marginTop: 20 }}>
              <Col>
                <Button onClick={() => resetForm()}>Reset</Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={handleProceedToStep2}
                  size="large"
                >
                  Proceed to Step 2
                </Button>
              </Col>
            </Row>
          </Form>
        )}

        {/* STEP 2: Add Running Sizes */}
        {currentStep === 2 && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  <Text strong>Selected Customer:</Text>{" "}
                  <Tag color="blue">
                    {customer?.find((c) => c.id === commonFields.customer)?.name}
                  </Tag>
                </div>
                <div>
                  <Text strong>Selected Sub Category:</Text>{" "}
                  <Tag color="green">
                    {subCategory?.find((s) => s.id === commonFields.subcategory)?.name}
                  </Tag>
                </div>
                {Object.entries(commonFields).some(
                  ([key, val]) =>
                    val !== null &&
                    !["subcategory", "customer", "isPrinting", "isCoating", "layerSelector"].includes(
                      key
                    ) &&
                    val !== ""
                ) && (
                  <div>
                    <Text strong>Common Fields Set:</Text>
                    <div style={{ marginTop: 8 }}>
                      {Object.entries(commonFields)
                        .filter(
                          ([key, val]) =>
                            val !== null &&
                            !["subcategory", "customer", "isPrinting", "isCoating", "layerSelector"].includes(
                              key
                            ) &&
                            val !== ""
                        )
                        .map(([key, val]) => (
                          <Tag key={key} style={{ marginRight: 8, marginBottom: 8 }}>
                            {key}: {val}
                          </Tag>
                        ))}
                    </div>
                  </div>
                )}
              </Space>
            </div>

            <Divider>Add Running Sizes Below</Divider>

            {rows.length === 0 ? (
              <Empty description="No running sizes added yet" style={{ margin: "40px 0" }} />
            ) : (
              <Table
                columns={tableColumns}
                dataSource={rows}
                rowKey="key"
                scroll={{ x: 1400 }}
                pagination={false}
              />
            )}

            <Row justify="center" style={{ marginTop: 20, marginBottom: 20 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={addRow}
                size="large"
              >
                Add Row
              </Button>
            </Row>

            <Row gutter={16} justify="end" style={{ marginTop: 20 }}>
              <Col>
                <Button onClick={handleBackToStep1}>Back to Step 1</Button>
              </Col>
              <Col>
                <Button
                  onClick={() => setRows([])}
                  icon={<ClearOutlined />}
                >
                  Clear All
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleBulkSubmit}
                  loading={loading}
                  size="large"
                >
                  Create {rows.length} Running Sizes
                </Button>
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BulkAddRunningSize;
